/* UAE filing dates for the landing-page product mocks.
   ---------------------------------------------------------------------------
   The reference prototypes hard-coded "1–20 Jul 2026 · 64 days left" into the
   dashboard mock. Hard-coded dates go stale — and a compliance page showing a
   deadline that has already passed undermines the exact thing it is selling.

   Derived from the real rule instead: a UAE quarterly VAT return is due within
   28 days of the end of the tax period, so the filing window is the first 28
   days of the month after each quarter ends. */

export type VatWindow = {
  /** e.g. "Q3 (Jul–Sep 2026)" — the period being filed. */
  quarterLabel: string;
  /** e.g. "1–28 Oct 2026" — the window the return can be filed in. */
  windowLabel: string;
  /** e.g. "28 Oct 2026" — the deadline itself. */
  dueLabel: string;
  /** Whole days from today to the deadline (never negative). */
  daysLeft: number;
  /** Calendar year of the period being filed, for "<year> to date" copy. */
  year: number;
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * The next VAT filing window from `now` — i.e. the first quarter whose 28-day
 * filing window has not closed yet.
 */
export function nextVatWindow(now: Date = new Date()): VatWindow {
  // Start one quarter BACK from the one containing `now`: during the 28 days after
  // a quarter ends, that quarter's window is the one still open, so starting at the
  // current quarter would skip it and show a deadline three months out.
  let year = now.getFullYear();
  let q = Math.floor(now.getMonth() / 3) - 1;
  if (q < 0) { q = 3; year -= 1; }

  for (let guard = 0; guard < 8; guard++) {
    const endMonth = q * 3 + 2;                    // last month of the quarter (0-based)
    const dueYear = endMonth === 11 ? year + 1 : year;
    const dueMonth = (endMonth + 1) % 12;          // month the window falls in
    const due = new Date(dueYear, dueMonth, 28, 23, 59, 59);
    if (due.getTime() > now.getTime()) {
      const startMonth = q * 3;
      const msLeft = due.getTime() - now.getTime();
      return {
        quarterLabel: `Q${q + 1} (${MONTHS[startMonth]}–${MONTHS[endMonth]} ${year})`,
        windowLabel: `1–28 ${MONTHS[dueMonth]} ${dueYear}`,
        dueLabel: `28 ${MONTHS[dueMonth]} ${dueYear}`,
        daysLeft: Math.max(0, Math.ceil(msLeft / 86_400_000)),
        year,
      };
    }
    q += 1;
    if (q > 3) { q = 0; year += 1; }
  }

  // Unreachable in practice — the loop above always resolves within one year.
  return { quarterLabel: `Q1 (Jan–Mar ${year})`, windowLabel: `1–28 Apr ${year}`, dueLabel: `28 Apr ${year}`, daysLeft: 0, year };
}
